import axios from "axios";
import { useCallback, useEffect, useState } from "react";

import type {
  GetAnswersResponseDto,
  GetScheduledQuestionnaireResponseDto,
} from "@/generated/model";
import {
  getAnswersControllerGetAnswers,
  getScheduledQuestionnaireControllerGetScheduledQuestionnaire,
} from "@/generated/scheduled-questionnaires/scheduled-questionnaires";
import { offlineDb } from "@/lib/offline/db";

interface CachedDetail {
  questionnaire: GetScheduledQuestionnaireResponseDto;
  answers: GetAnswersResponseDto["answers"];
}

interface UseOfflineScheduledQuestionnaireDetailResult {
  questionnaire: GetScheduledQuestionnaireResponseDto | null;
  answers: GetAnswersResponseDto["answers"];
  isLoading: boolean;
  isOffline: boolean;
  error: Error | null;
  /** HTTP status of `error`, when it came from the server. */
  errorStatus: number | undefined;
  refetch: () => Promise<void>;
}

/** Fresh detail plus whether the answers call failed on its own. */
interface FetchedDetail extends CachedDetail {
  answersFailed: boolean;
}

/**
 * Offline-first hook for fetching a single scheduled questionnaire with its answers.
 *
 * Flow:
 * 1. Immediately return cached data from IndexedDB (if available)
 * 2. If online, fetch fresh data from server in background
 * 3. Update UI with fresh data when it arrives
 * 4. Save fresh data to IndexedDB cache
 */

/**
 * Prefetch a single scheduled questionnaire detail and cache it for offline use.
 * This is called in the background when the checklists list page loads.
 *
 * @returns true if prefetch was successful, false otherwise
 */
export async function prefetchScheduledQuestionnaireDetail(
  scheduledQuestionnaireId: string,
): Promise<boolean> {
  if (!navigator.onLine) return false;

  try {
    // Check if already cached and fresh
    const cached = await offlineDb.questionnaireDetails.get(
      scheduledQuestionnaireId,
    );
    if (cached) {
      const STALE_TIME = 5 * 60 * 1000; // 5 minutes
      if (Date.now() - cached.cachedAt < STALE_TIME) {
        // Already cached and fresh, skip prefetch
        return true;
      }
    }

    // Fetch API data and answers in parallel
    const [questionnaireResponse, answersResponse] = await Promise.all([
      getScheduledQuestionnaireControllerGetScheduledQuestionnaire(
        scheduledQuestionnaireId,
      ),
      getAnswersControllerGetAnswers(scheduledQuestionnaireId),
    ]);

    const data: CachedDetail = {
      questionnaire: questionnaireResponse.data,
      answers: answersResponse.data?.answers || [],
    };

    // Save to cache
    await offlineDb.questionnaireDetails.put({
      id: scheduledQuestionnaireId,
      data: data as unknown,
      cachedAt: Date.now(),
    });

    return true;
  } catch (err) {
    console.error(
      `Failed to prefetch questionnaire ${scheduledQuestionnaireId}:`,
      err,
    );
    return false;
  }
}

/**
 * Prefetch multiple scheduled questionnaire details in the background.
 * Uses a small concurrency limit to avoid overwhelming the server.
 *
 * @param ids - Array of scheduled questionnaire IDs to prefetch
 * @param concurrency - Maximum number of concurrent requests (default: 3)
 */
export async function prefetchScheduledQuestionnaireDetails(
  ids: string[],
  concurrency: number = 3,
): Promise<void> {
  if (!navigator.onLine || ids.length === 0) return;

  // Process in batches to limit concurrency
  for (let i = 0; i < ids.length; i += concurrency) {
    const batch = ids.slice(i, i + concurrency);
    await Promise.all(
      batch.map((id) => prefetchScheduledQuestionnaireDetail(id)),
    );
  }
}

export function useOfflineScheduledQuestionnaireDetail(
  scheduledQuestionnaireId: string | null,
): UseOfflineScheduledQuestionnaireDetailResult {
  const [questionnaire, setQuestionnaire] =
    useState<GetScheduledQuestionnaireResponseDto | null>(null);
  const [answers, setAnswers] = useState<GetAnswersResponseDto["answers"]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined);

  // Load cached data from IndexedDB
  const loadFromCache = useCallback(async (): Promise<{
    data: CachedDetail;
    timestamp: number;
  } | null> => {
    if (!scheduledQuestionnaireId) return null;

    try {
      const cached = await offlineDb.questionnaireDetails.get(
        scheduledQuestionnaireId,
      );
      if (cached) {
        const data = cached.data as CachedDetail;
        return {
          data,
          timestamp: cached.cachedAt,
        };
      }
    } catch (err) {
      console.error("Failed to load questionnaire detail from cache:", err);
    }
    return null;
  }, [scheduledQuestionnaireId]);

  // Save data to IndexedDB cache
  const saveToCache = useCallback(
    async (data: CachedDetail) => {
      if (!scheduledQuestionnaireId) return;

      try {
        await offlineDb.questionnaireDetails.put({
          id: scheduledQuestionnaireId,
          data: data as unknown,
          cachedAt: Date.now(),
        });
      } catch (err) {
        console.error("Failed to save questionnaire detail to cache:", err);
      }
    },
    [scheduledQuestionnaireId],
  );

  // Fetch fresh data from server. Only the questionnaire itself is fatal — a
  // failing answers call must not take the whole detail view down with it.
  const fetchFromServer =
    useCallback(async (): Promise<FetchedDetail | null> => {
      if (!scheduledQuestionnaireId) return null;

      const [questionnaireResult, answersResult] = await Promise.allSettled([
        getScheduledQuestionnaireControllerGetScheduledQuestionnaire(
          scheduledQuestionnaireId,
        ),
        getAnswersControllerGetAnswers(scheduledQuestionnaireId),
      ]);

      if (questionnaireResult.status === "rejected") {
        console.error(
          "Failed to fetch questionnaire detail from server:",
          questionnaireResult.reason,
        );
        throw questionnaireResult.reason;
      }

      if (answersResult.status === "rejected") {
        console.error(
          "Failed to fetch questionnaire answers from server:",
          answersResult.reason,
        );
      }

      return {
        questionnaire: questionnaireResult.value.data,
        answers:
          answersResult.status === "fulfilled"
            ? answersResult.value.data?.answers || []
            : [],
        answersFailed: answersResult.status === "rejected",
      };
    }, [scheduledQuestionnaireId]);

  // Apply fresh data. When only the answers call failed we keep the questionnaire
  // but leave the existing answers and the cache alone, so a transient failure
  // can't blank a good answer list.
  const applyFreshData = useCallback(
    async (freshData: FetchedDetail) => {
      setQuestionnaire(freshData.questionnaire);
      if (freshData.answersFailed) return;

      setAnswers(freshData.answers);
      await saveToCache({
        questionnaire: freshData.questionnaire,
        answers: freshData.answers,
      });
    },
    [saveToCache],
  );

  const recordError = useCallback((err: unknown) => {
    setError(err instanceof Error ? err : new Error("Failed to fetch"));
    setErrorStatus(axios.isAxiosError(err) ? err.response?.status : undefined);
  }, []);

  // Main refetch function
  const refetch = useCallback(async () => {
    if (!scheduledQuestionnaireId) return;

    setError(null);
    setErrorStatus(undefined);

    if (!navigator.onLine) {
      setIsOffline(true);
      return;
    }

    try {
      const freshData = await fetchFromServer();
      if (freshData) {
        await applyFreshData(freshData);
      }
    } catch (err) {
      recordError(err);
    }
  }, [scheduledQuestionnaireId, fetchFromServer, applyFreshData, recordError]);

  // Initial load effect
  useEffect(() => {
    let isMounted = true;

    async function initialize() {
      if (!scheduledQuestionnaireId) {
        setIsLoading(false);
        setQuestionnaire(null);
        setAnswers([]);
        return;
      }

      setIsLoading(true);
      setError(null);
      setErrorStatus(undefined);

      // Check offline status after mount
      const offline = !navigator.onLine;
      setIsOffline(offline);

      // Step 1: Load cached data immediately
      const cached = await loadFromCache();
      if (!isMounted) return;

      if (cached) {
        setQuestionnaire(cached.data.questionnaire);
        setAnswers(cached.data.answers);
        setIsLoading(false);
      } else {
        // Drop the previous checklist's answers: applyFreshData keeps whatever is
        // in state when the answers call fails, and that must never be another
        // checklist's answers.
        setAnswers([]);
      }

      // Step 2: If online, fetch fresh data in background
      if (!offline) {
        try {
          const freshData = await fetchFromServer();
          if (freshData && isMounted) {
            await applyFreshData(freshData);
          }
        } catch (err) {
          if (isMounted) {
            // Only set error if we don't have cached data
            if (!cached) {
              recordError(err);
            }
          }
        }
      } else if (!cached) {
        // Offline with no cache - this is an error state
        if (isMounted) {
          setError(new Error("No cached data available offline"));
        }
      }

      if (isMounted) {
        setIsLoading(false);
      }
    }

    initialize();

    return () => {
      isMounted = false;
    };
  }, [
    scheduledQuestionnaireId,
    loadFromCache,
    fetchFromServer,
    applyFreshData,
    recordError,
  ]);

  // Online/offline listeners
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      refetch();
    };

    const handleOffline = () => {
      setIsOffline(true);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [refetch]);

  return {
    questionnaire,
    answers,
    isLoading,
    isOffline,
    error,
    errorStatus,
    refetch,
  };
}
